import React, { useState, useEffect, useRef } from "react";
import {
  Image,
  Modal,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  ScrollView,
  StyleSheet,
  Animated,
  Dimensions,
} from "react-native";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { AppColors } from "../../../../utility/AppColors";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";
import { AppImages } from "../../../../utility/AppImages";
import { Picker } from "@react-native-picker/picker";

const { height: screenHeight } = Dimensions.get("window");

interface Platform {
  id: string;
  platform: string;
  link: string;
}

interface UnlockEventCreationModalProps {
  showPopup: boolean;
  closePopup: () => void;
}

// Platform data from AddSocialLink
const socialMediaData = [
  {
    name: "Instagram",
    value: "instagram",
    placeholder: "https://www.instagram.com/",
    image: AppImages.icSocialInsta,
  },
  {
    name: "YouTube",
    value: "youtube",
    placeholder: "https://www.youtube.com/",
    image: AppImages.icSocialYoutube,
  },
  {
    name: "Twitch",
    value: "twitch",
    placeholder: "https://www.twitch.com/",
    image: AppImages.icSocialTwitch,
  },
  {
    name: "Discord",
    value: "discord",
    placeholder: "https://www.discord.com/",
    image: AppImages.icSocialDiscord,
  },
  {
    name: "X (Twitter)",
    value: "x",
    placeholder: "https://www.x.com/",
    image: AppImages.icSocialTwitter,
  },
  {
    name: "LinkedIn",
    value: "linkedIn",
    placeholder: "https://www.linkedIn.com/",
    image: AppImages.icLinkedIn,
  },
  {
    name: "Facebook",
    value: "facebook",
    placeholder: "https://www.facebook.com/",
    image: AppImages.icSocialFacebookSvg,
  },
];

const UnlockEventCreationModal: React.FC<UnlockEventCreationModalProps> = ({
  showPopup,
  closePopup,
}) => {
  const deviceSize = useDeviceSize();
  const isMobile = deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;
  const slideAnim = useRef(new Animated.Value(screenHeight)).current;
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1: Phone number
  const [countryCode, setCountryCode] = useState("+1");
  const [phoneNumber, setPhoneNumber] = useState("");

  // Step 2: OTP
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpTimer, setOtpTimer] = useState(34);
  const otpRefs = useRef<Array<TextInput | null>>([]);

  // Step 3: Social links
  const [platforms, setPlatforms] = useState<Platform[]>([
    { id: "1", platform: "youtube", link: "" },
  ]);

  // Bottom sheet animation
  useEffect(() => {
    if (showPopup && isMobile) {
      Animated.spring(slideAnim, {
        toValue: 0,
        useNativeDriver: true,
        tension: 65,
        friction: 10,
      }).start();
    } else if (!showPopup && isMobile) {
      Animated.timing(slideAnim, {
        toValue: screenHeight,
        duration: 250,
        useNativeDriver: true,
      }).start();
    }
  }, [showPopup, isMobile]);

  // OTP Timer countdown
  useEffect(() => {
    if (currentStep === 2 && otpTimer > 0) {
      const timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [currentStep, otpTimer]);

  const handleSendCode = () => {
    if (phoneNumber.trim()) {
      setCurrentStep(2);
      setOtpTimer(34);
    }
  };

  const handleOtpChange = (value: string, index: number) => {
    // Handle paste
    if (value.length > 1) {
      const pastedValue = value.slice(0, 6);
      const newOtp = [...otp];
      for (let i = 0; i < pastedValue.length && index + i < 6; i++) {
        newOtp[index + i] = pastedValue[i];
      }
      setOtp(newOtp);

      // Focus last filled input
      const lastIndex = Math.min(index + pastedValue.length - 1, 5);
      otpRefs.current[lastIndex]?.focus();
      return;
    }

    // Handle single digit input
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 5) {
        otpRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleOtpKeyPress = (e: any, index: number) => {
    // Handle backspace
    if (e.nativeEvent.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = () => {
    const enteredOtp = otp.join("");
    if (enteredOtp === "123456") {
      setCurrentStep(3);
    } else {
      alert("Invalid OTP. Use 123456 for testing.");
    }
  };

  const handleAddPlatform = () => {
    setPlatforms([
      ...platforms,
      { id: Date.now().toString(), platform: "", link: "" },
    ]);
  };

  const handleRemovePlatform = (id: string) => {
    if (platforms.length > 1) {
      setPlatforms(platforms.filter((p) => p.id !== id));
    }
  };

  const handlePlatformChange = (id: string, field: "platform" | "link", value: string) => {
    setPlatforms(
      platforms.map((p) =>
        p.id === id ? { ...p, [field]: value } : p
      )
    );
  };

  const handleSendSocials = () => {
    console.log("Form Data:", {
      phoneNumber: `${countryCode}${phoneNumber}`,
      platforms: platforms.filter((p) => p.platform && p.link),
    });
    setCurrentStep(4);
  };

  const handleClose = () => {
    // Reset state
    setCurrentStep(1);
    setPhoneNumber("");
    setOtp(["", "", "", "", "", ""]);
    setPlatforms([{ id: "1", platform: "youtube", link: "" }]);
    closePopup();
  };

  const handleWatchEvents = () => {
    handleClose();
  };

  // Step 1: Phone Number Verification
  const renderStep1 = () => (
    <View style={{ width: "100%" }}>
      <Text style={[styles.title, deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM ? AppFontStyle.BOLD_ANYBODY_20 : AppFontStyle.BOLD_ANYBODY_24]}>
        Unlock Event Creation
      </Text>

      <Text style={[styles.subtitle, AppFontStyle.REGULAR_ANYBODY_14]}>
        To host your own battles on Kallout, we just need a little extra info. This helps us keep events legit, connect players, and boost your visibility
      </Text>

      <View style={{ marginTop: 20 }}>
        <Text style={[styles.label, AppFontStyle.REGULAR_ANYBODY_14]}>
          Verify your phone number
        </Text>
        <Text style={[styles.fieldLabel, AppFontStyle.REGULAR_ANYBODY_12]}>
          Phone Number <Text style={{ color: "#EF4444" }}>*</Text>
        </Text>

        <View style={styles.phoneInputContainer}>
          <View style={styles.countryCodeContainer}>
            <Text style={[AppFontStyle.REGULAR_ANYBODY_14, { color: "#FFF" }]}>
              {countryCode}
            </Text>
            <Text style={{ color: "#FFF", marginLeft: 8 }}>▼</Text>
          </View>

          <TextInput
            style={[styles.phoneInput, AppFontStyle.REGULAR_ANYBODY_14]}
            placeholder="34 434 4353"
            placeholderTextColor="#6B7280"
            value={phoneNumber}
            onChangeText={setPhoneNumber}
            keyboardType="phone-pad"
          />
        </View>
      </View>

      <TouchableOpacity
        style={styles.sendCodeButton}
        onPress={handleSendCode}
      >
        <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: "#FFF" }]}>
          Send code
        </Text>
      </TouchableOpacity>
    </View>
  );

  // Step 2: OTP Verification
  const renderStep2 = () => (
    <View style={{ width: "100%" }}>
      <Text style={[styles.title, deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM ? AppFontStyle.BOLD_ANYBODY_20 : AppFontStyle.BOLD_ANYBODY_24]}>
        Unlock Event Creation
      </Text>

      <Text style={[styles.subtitle, AppFontStyle.REGULAR_ANYBODY_14]}>
        To host your own battles on Kallout, we just need a little extra info. This helps us keep events legit, connect players, and boost your visibility
      </Text>

      <View style={{ marginTop: 20 }}>
        <Text style={[styles.label, AppFontStyle.REGULAR_ANYBODY_14]}>
          Verify your phone number
        </Text>
        <Text style={[styles.fieldLabel, AppFontStyle.REGULAR_ANYBODY_12]}>
          Phone Number <Text style={{ color: "#EF4444" }}>*</Text>
        </Text>

        <View style={styles.phoneInputContainer}>
          <View style={styles.countryCodeContainer}>
            <Text style={[AppFontStyle.REGULAR_ANYBODY_14, { color: "#FFF" }]}>
              {countryCode}
            </Text>
            <Text style={{ color: "#FFF", marginLeft: 8 }}>▼</Text>
          </View>

          <TextInput
            style={[styles.phoneInput, AppFontStyle.REGULAR_ANYBODY_14]}
            value={phoneNumber}
            editable={false}
          />
        </View>

        <Text style={[styles.otpLabel, AppFontStyle.REGULAR_ANYBODY_14, { marginTop: 20 }]}>
          Enter 6 digit code sent to your number
        </Text>
        <Text style={[styles.fieldLabel, AppFontStyle.REGULAR_ANYBODY_12]}>
          Enter OTP <Text style={{ color: "#EF4444" }}>*</Text>
        </Text>

        <View style={styles.otpContainer}>
          {otp.map((digit, index) => (
            <TextInput
              key={index}
              ref={(ref) => (otpRefs.current[index] = ref)}
              style={styles.otpInput}
              value={digit}
              onChangeText={(value) => handleOtpChange(value, index)}
              onKeyPress={(e) => handleOtpKeyPress(e, index)}
              keyboardType="number-pad"
              maxLength={1}
              selectTextOnFocus
            />
          ))}
        </View>

        <Text style={[AppFontStyle.REGULAR_ANYBODY_12, { color: "#FFF", textAlign: "center", marginTop: 12 }]}>
          OTP will expires in : {otpTimer}
        </Text>
      </View>

      <TouchableOpacity
        style={styles.sendCodeButton}
        onPress={handleVerifyOtp}
      >
        <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: "#FFF" }]}>
          Verify
        </Text>
      </TouchableOpacity>
    </View>
  );

  // Step 3: Link Socials
  const renderStep3 = () => (
    <View style={{ width: "100%" }}>
      <Text style={[styles.title, deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM ? AppFontStyle.BOLD_ANYBODY_20 : AppFontStyle.BOLD_ANYBODY_24]}>
        Link Your Socials
      </Text>

      <Text style={[styles.subtitle, AppFontStyle.REGULAR_ANYBODY_14]}>
        Number verified! Now let's connect your socials.
      </Text>

      <View style={{ marginTop: 15 }}>
        <Text style={[styles.label, AppFontStyle.REGULAR_ANYBODY_14]}>
          Choose your main platform(s) and drop the links. This helps us confirm your creator status.
        </Text>

        <ScrollView style={{ maxHeight: 350, marginTop: 15 }}>
          {platforms.map((platform, index) => (
            <View key={platform.id} style={{ marginBottom: 12 }}>
              <View style={styles.socialRowContainer}>
                {/* Platform Dropdown */}
                <View style={styles.platformPickerContainer}>
                  <Picker
                    selectedValue={platform.platform}
                    onValueChange={(value) =>
                      handlePlatformChange(platform.id, "platform", value)
                    }
                    style={styles.platformPicker}
                    dropdownIconColor="#FFF"
                  >
                    <Picker.Item label="Select a platform" value="" />
                    {socialMediaData.map((social) => (
                      <Picker.Item
                        key={social.value}
                        label={social.name}
                        value={social.value}
                      />
                    ))}
                  </Picker>
                </View>

                {/* Link Input */}
                <TextInput
                  style={[styles.socialLinkInput, AppFontStyle.REGULAR_ANYBODY_14]}
                  placeholder="Link"
                  placeholderTextColor="#6B7280"
                  value={platform.link}
                  onChangeText={(value) =>
                    handlePlatformChange(platform.id, "link", value)
                  }
                />

                {/* Remove button (only show if more than 1 platform) */}
                {platforms.length > 1 && (
                  <TouchableOpacity
                    onPress={() => handleRemovePlatform(platform.id)}
                    style={styles.removeButton}
                  >
                    <Text style={{ color: "#EF4444", fontSize: 20 }}>×</Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>
          ))}
        </ScrollView>

        <TouchableOpacity
          style={{ alignItems: "center", marginTop: 10 }}
          onPress={handleAddPlatform}
        >
          <Text style={[AppFontStyle.REGULAR_ANYBODY_14, { color: "#DC2626" }]}>
            + Add Another
          </Text>
        </TouchableOpacity>
      </View>

      <TouchableOpacity
        style={styles.sendCodeButton}
        onPress={handleSendSocials}
      >
        <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: "#FFF" }]}>
          Send
        </Text>
      </TouchableOpacity>
    </View>
  );

  // Step 4: Application Submitted
  const renderStep4 = () => (
    <View style={{ width: "100%", alignItems: "center" }}>
      <Text style={[styles.title, deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM ? AppFontStyle.BOLD_ANYBODY_20 : AppFontStyle.BOLD_ANYBODY_24]}>
        Application Submitted!
      </Text>

      <View style={{ marginVertical: 30 }}>
        <Text style={{ fontSize: 60 }}>🎉</Text>
      </View>

      <Text style={[AppFontStyle.REGULAR_ANYBODY_14, { color: "#FFF", textAlign: "center", marginBottom: 8 }]}>
        Thanks for applying.
      </Text>
      <Text style={[AppFontStyle.REGULAR_ANYBODY_14, { color: "#FFF", textAlign: "center", lineHeight: 22 }]}>
        Our team will review your info and get back to you. Stay tuned — we'll notify you once your status is confirmed.
      </Text>

      <TouchableOpacity
        style={[styles.sendCodeButton, { marginTop: 30 }]}
        onPress={handleWatchEvents}
      >
        <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: "#FFF" }]}>
          Watch Events
        </Text>
      </TouchableOpacity>
    </View>
  );

  // Mobile bottom sheet rendering
  if (isMobile) {
    return (
      <Modal
        transparent={true}
        visible={showPopup}
        onRequestClose={handleClose}
        animationType="none"
      >
        <View style={styles.mobileOverlay}>
          <TouchableOpacity
            style={styles.mobileBackdrop}
            activeOpacity={1}
            onPress={handleClose}
          />
          <Animated.View
            style={[
              styles.mobileBottomSheet,
              {
                transform: [{ translateY: slideAnim }],
              },
            ]}
          >
            <TouchableOpacity
              style={styles.closeButton}
              onPress={handleClose}
            >
              <Image
                source={AppImages.icCloseWhite}
                style={{ width: 24, height: 24 }}
              />
            </TouchableOpacity>

            <ScrollView
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ padding: 20, paddingTop: 40 }}
            >
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && renderStep4()}
            </ScrollView>
          </Animated.View>
        </View>
      </Modal>
    );
  }

  // Desktop modal rendering
  return (
    <Modal
      transparent={true}
      visible={showPopup}
      onRequestClose={handleClose}
      animationType="fade"
    >
      <View style={styles.overlay}>
        <View
          style={[
            styles.container,
            {
              width: "40%",
              padding: 20,
            },
          ]}
        >
          <TouchableOpacity
            style={styles.closeButton}
            onPress={handleClose}
          >
            <Image
              source={AppImages.icCloseWhite}
              style={{ width: 24, height: 24 }}
            />
          </TouchableOpacity>

          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ flexGrow: 1, alignItems: "center" }}
          >
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.76)",
    justifyContent: "center",
    alignItems: "center",
  },
  container: {
    borderRadius: 8,
    borderWidth: 1.6,
    borderColor: "rgba(239, 68, 68, 0.50)",
    backgroundColor: "#111827",
    padding: 20,
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    maxHeight: "90%",
  },
  mobileOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.76)",
  },
  mobileBackdrop: {
    flex: 1,
  },
  mobileBottomSheet: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#111827",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderWidth: 1.6,
    borderColor: "rgba(239, 68, 68, 0.50)",
    maxHeight: screenHeight * 0.9,
    minHeight: screenHeight * 0.5,
  },
  closeButton: {
    position: "absolute",
    top: 16,
    right: 16,
    zIndex: 10,
    padding: 8,
  },
  title: {
    color: "#FFF",
    fontWeight: "500",
    lineHeight: 26,
    marginTop: 20,
  },
  subtitle: {
    color: "#FFF",
    fontSize: 18,
    fontWeight: "500",
    lineHeight: 30,
    marginTop: 12,
  },
  label: {
    color: "#FFF",
    marginBottom: 12,
  },
  fieldLabel: {
    color: "#FFF",
    marginBottom: 8,
    marginTop: 12,
  },
  phoneInputContainer: {
    flexDirection: "row",
    borderRadius: 8,
    borderWidth: 0.8,
    borderColor: "#374151",
    backgroundColor: "#1F2937",
    overflow: "hidden",
  },
  countryCodeContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderRightWidth: 0.8,
    borderRightColor: "#374151",
  },
  phoneInput: {
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: 12,
    color: "#FFF",
  },
  sendCodeButton: {
    borderRadius: 6,
    backgroundColor: "#DC2626",
    height: 40,
    paddingHorizontal: 24,
    justifyContent: "center",
    alignItems: "center",
    marginTop: 20,
  },
  otpLabel: {
    color: "#FFF",
    marginBottom: 8,
  },
  otpContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 8,
    marginTop: 12,
  },
  otpInput: {
    width: 48,
    height: 56,
    borderRadius: 8,
    borderWidth: 0.8,
    borderColor: "#374151",
    backgroundColor: "#1F2937",
    textAlign: "center",
    color: "#FFF",
    fontSize: 20,
  },
  socialRowContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  platformPickerContainer: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 0.8,
    borderColor: "#374151",
    backgroundColor: "#374151",
    overflow: "hidden",
  },
  platformPicker: {
    color: "#FFF",
    height: 45,
  },
  socialLinkInput: {
    flex: 1.5,
    borderRadius: 8,
    borderWidth: 0.8,
    borderColor: "#374151",
    backgroundColor: "#374151",
    paddingHorizontal: 12,
    paddingVertical: 12,
    color: "#FFF",
    height: 45,
  },
  removeButton: {
    width: 30,
    height: 30,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default UnlockEventCreationModal;
